import type { TravelPersonalityType } from '../../types/wrapped'

// Diamond gradient backgrounds from Figma design
// Uses 4 linear gradients positioned in each quadrant to create the diamond effect
// Using calc(50% + 1px) to ensure quadrants overlap and avoid visible seams
export const createDiamondGradient = (edgeColor: string, centerColor: string) => `
  linear-gradient(to bottom right, ${edgeColor} 15%, ${centerColor} 50%) bottom right / calc(50% + 1px) calc(50% + 1px) no-repeat,
  linear-gradient(to bottom left, ${edgeColor} 15%, ${centerColor} 50%) bottom left / calc(50% + 1px) calc(50% + 1px) no-repeat,
  linear-gradient(to top left, ${edgeColor} 15%, ${centerColor} 50%) top left / calc(50% + 1px) calc(50% + 1px) no-repeat,
  linear-gradient(to top right, ${edgeColor} 15%, ${centerColor} 50%) top right / calc(50% + 1px) calc(50% + 1px) no-repeat
`

export const PERSONALITY_GRADIENTS: Record<TravelPersonalityType, string> = {
  'the-sun-chaser': createDiamondGradient('#FCDCE1', '#E49F4E'),
  'the-wellness-wanderer': createDiamondGradient('#FDF1E3', '#8CD9DE'),
  'the-nature-roamer': createDiamondGradient('#E6F6F2', '#99E0D1'),
  'the-bond-builder': createDiamondGradient('#CCEDE6', '#30C5CE'),
  'the-romance-storyteller': createDiamondGradient('#FCDCE1', '#F5A388'),
  'the-trend-explorer': createDiamondGradient('#FCDCE1', '#C4C3F0'),
  'the-gourmet-hunter': createDiamondGradient('#FCDCE1', '#FDE2C3'),
  'the-elite-traveller': createDiamondGradient('#FDF1E3', '#CCDDDB'),
  'the-curious-explorer': createDiamondGradient('#CDF4F1', '#00F1BD'),
}

export const PERSONALITY_NAMES: Record<TravelPersonalityType, string> = {
  'the-bond-builder': 'The Bond Builder',
  'the-curious-explorer': 'The Curious Explorer',
  'the-elite-traveller': 'The Elite Traveller',
  'the-nature-roamer': 'The Nature Roamer',
  'the-romance-storyteller': 'The romantic retreat',
  'the-sun-chaser': 'The Sun Chaser',
  'the-wellness-wanderer': 'The Wellness Wanderer',
  'the-trend-explorer': 'The Trend Explorer',
  'the-gourmet-hunter': 'The Gourmet Hunter',
}

export const PERSONALITY_IMAGE_IDS: Record<TravelPersonalityType, string> = {
  'the-bond-builder': 'personality-bond-builder',
  'the-curious-explorer': 'personality-curious-explorer',
  'the-elite-traveller': 'personality-elite-traveller',
  'the-nature-roamer': 'personality-nature-roamer',
  'the-romance-storyteller': 'personality-romance-storyteller',
  'the-sun-chaser': 'personality-sun-chaser',
  'the-wellness-wanderer': 'personality-wellness-wanderer',
  'the-trend-explorer': 'personality-trend-explorer',
  'the-gourmet-hunter': 'personality-gourmet-hunter',
}

export const PERSONALITY_DESCRIPTIONS: Record<TravelPersonalityType, string> = {
  'the-bond-builder': 'You loved travelling with friends and family, choosing escapes that brought people closer through shared moments and meaningful memories.',
  'the-curious-explorer': 'You discovered a bit of everything that inspires you, following your curiosity to new destinations.',
  'the-elite-traveller': 'You were drawn to premium escapes and exclusive experiences, valuing exceptional details and elevated comforts at every step.',
  'the-nature-roamer': 'You were drawn to wide open spaces and outdoor adventures, where nature set the pace and every day felt refreshing.',
  'the-romance-storyteller': 'You were inspired by intimate escapes, where beautiful settings created space for connection and special moments.',
  'the-sun-chaser': 'You loved sun-soaked escapes, choosing destinations that felt warm, uplifting and perfect for switching off.',
  'the-wellness-wanderer': 'You sought calm, balance and time to slow down, gravitating towards escapes that helped you relax and reset.',
  'the-trend-explorer': 'You gravitated towards stylish stays and vibrant cities, always curious about what was new, exciting and worth discovering.',
  'the-gourmet-hunter': 'You travelled with your taste buds, seeking destinations where food, drink and memorable dining experiences took centre stage.',
}

// TODO update with calculated values
export const PERSONALITY_PERCENTILES: Record<TravelPersonalityType, number> = {
  'the-bond-builder': 8,
  'the-curious-explorer': 1,
  'the-elite-traveller': 1,
  'the-nature-roamer': 1,
  'the-romance-storyteller': 7,
  'the-sun-chaser': 65,
  'the-wellness-wanderer': 1,
  'the-trend-explorer': 18,
  'the-gourmet-hunter': 1,
}
